import * as Sentry from '@sentry/vue'
import store from '@/store'
import redactEmails from './redactEmails'

export type ServerError = {
  type?: string
  message?: string
  messages?: string[]
  metadata?: Record<string, any>
  transport?: boolean
}

type ReportSubject = {
  scene: string
  checkin?: Record<string, any> | null
  booking?: Record<string, any> | null
  isPropertyDevice?: boolean
}

export type CheckinErrorReport = {
  errorType: string
  context: Record<string, unknown>
  fingerprint: string[]
  message: string
}

/**
 * Builds what gets reported, separately from sending it.
 *
 * Kept pure and exported so the payload can be pinned by a test: the DSN is only set in
 * production (`.env.staging` and `.env.qa` leave it empty, which makes `Sentry.init` a no-op),
 * so none of this is observable before release. See ENG-2910.
 */
export function buildCheckinErrorReport(
  error: ServerError | null | undefined,
  { scene, checkin, booking, isPropertyDevice }: ReportSubject
): CheckinErrorReport {
  const errorType =
    error?.type ?? (error?.transport ? 'NetworkError' : 'Unknown')

  const serverMessage = (error?.messages ?? [error?.message])
    .filter(Boolean)
    .join(' | ')

  return {
    errorType,
    context: {
      checkinId: checkin?.id,
      guestId: checkin?.guest?.id,
      bookingCode: booking?.code,
      step: checkin?.step,
      status: checkin?.status,
      isPropertyDevice,
      // Ids only. These payloads are *about* a guest's email address, so anything carried over
      // from the server's text goes through redactEmails first.
      serverMessage: serverMessage ? redactEmails(serverMessage) : undefined,
    },
    // One Sentry issue per dead end, so frequency across the estate is queryable.
    fingerprint: ['checkin-dead-end', scene, errorType],
    message: `checkin dead end: ${scene} / ${errorType}`,
  }
}

/**
 * Reports a point where the guest could not continue.
 *
 * Until ENG-2910 nothing in this app reached Sentry: the store's `catch (e) { return [null, e] }`
 * tuple swallows the rejection before @sentry/vue's Vue error handler can see it, so the only
 * measure of how often guests get stuck was counting front-desk reports.
 *
 * Call this from the component, not the store -- the store cannot tell a handled outcome (an
 * expected GuestNotFound, a date-of-birth challenge) from a dead end, so capturing there would
 * bury the signal.
 */
export default function captureCheckinError(
  error: ServerError | null | undefined,
  { scene }: { scene: string }
) {
  const report = buildCheckinErrorReport(error, {
    scene,
    checkin: store.getters['checkins/checkin'],
    booking: store.getters['bookings/booking'],
    isPropertyDevice: store.getters.isPropertyDevice,
  })

  Sentry.withScope(scope => {
    scope.setTag('scene', scene)
    scope.setTag('errorType', report.errorType)
    scope.setContext('checkin', report.context)
    scope.setFingerprint(report.fingerprint)
    Sentry.captureException(new Error(report.message))
  })
}
